/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 18/05/22
 */

import WorkloadNumberField from '@/components/pages/Admin/Workload/WorkloadNumberField';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { usePutRequest } from '@/utils/useStatefulRequests';
import { Button, Flex, Heading, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { Form, Formik, FormikProps, FormikValues } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import * as Joi from 'joi';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { toTruncatedFixed } from '@/wpt-lib/utils/toTruncatedFixed';
import { mapWorkloadStaffTypeToString, WorkloadStaffType, WorkplanVersion } from '@/wpt-lib/interfaces/Workplan';
import { LabeledSelect } from '@/components/FormInputField';
import { defaultWorkloadAllocationValues } from '@/utils/workloadAllocation';

export interface UpdateAssignmentDTO {
    workload?: {
        teaching: number;
        research: number;
        service: number;
        senior: number;
        professional: number;
        staffType: WorkloadStaffType;
    };
}

interface UpdateWorkloadFormData {
    teaching: string;
    research: string;
    service: string;
    senior: string;
    professional: string;
    staffType: WorkloadStaffType;
}

const makeRound = (val: unknown): number => {
    const s = `${val}`;
    return parseFloat((parseFloat(s) / 100).toFixed(4));
};

const getDto = (data: UpdateWorkloadFormData): UpdateAssignmentDTO => {
    return {
        workload: {
            teaching: makeRound(data.teaching),
            research: makeRound(data.research),
            service: makeRound(data.service),
            senior: makeRound(data.senior),
            professional: makeRound(data.professional),
            staffType: data.staffType,
        },
    };
};

const getFormData = (workload: Assignment | WorkplanVersion): UpdateWorkloadFormData => {
    return {
        teaching: toTruncatedFixed(workload.teachingWorkloadPortion * 100, 2),
        research: toTruncatedFixed(workload.researchWorkloadPortion * 100, 2),
        service: toTruncatedFixed(workload.serviceWorkloadPortion * 100, 2),
        senior: toTruncatedFixed(workload.seniorStaffWorkloadPortion * 100, 2),
        professional: toTruncatedFixed(workload.professionalStaffWorkloadPortion * 100, 2),
        staffType: workload.staffType,
    };
};

const validate = (values: UpdateWorkloadFormData) => {
    const validatePercent = Joi.number().precision(2).min(0).max(100).required();
    const validateStaffType = Joi.string().valid(
        WorkloadStaffType.ACADEMIC,
        WorkloadStaffType.PROFESSIONAL,
        WorkloadStaffType.SENIOR,
    );

    const err = Joi.object({
        teaching: validatePercent,
        research: validatePercent,
        service: validatePercent,
        senior: validatePercent,
        professional: validatePercent,
        staffType: validateStaffType,
    }).validate(values, {
        abortEarly: false,
    });
    if (err.error) {
        return Object.fromEntries(err.error.details.map((error) => [error.path.join('.'), error.message]));
    } else if (
        parseFloat(values.teaching) +
            parseFloat(values.research) +
            parseFloat(values.service) +
            parseFloat(values.senior) +
            parseFloat(values.professional) !==
        100
    ) {
        return { total: 'Must add up to 100%' };
    }
};

export interface WorkloadEditorFormRef {
    getValues(): UpdateAssignmentDTO['workload'] | undefined;
    submit(): Promise<boolean>;
}

const WorkloadEditorForm = forwardRef<
    WorkloadEditorFormRef,
    {
        workload: Assignment | WorkplanVersion;
        currentAssignment: Assignment;
        saveButton?: boolean;
        loading?: boolean;
        saveUrl: string;
    }
>(({ workload, currentAssignment, saveUrl, loading, saveButton = true }, ref) => {
    const [allocationsDirty, setAllocationsDirty] = useState<boolean>(false);

    const {
        error: putError,
        loading: putLoading,
        makeRequest: makePutRequest,
        responseData,
    } = usePutRequest<Assignment, UpdateAssignmentDTO>();

    const handleSubmit = async (
        values: UpdateWorkloadFormData,
        helpers: { resetForm: FormikHelpers<UpdateWorkloadFormData>['resetForm'] },
    ) => {
        try {
            await makePutRequest(saveUrl, getDto(values), {
                params: {
                    assignment: currentAssignment?.id,
                },
            });
            helpers.resetForm({
                values,
            });
            setAllocationsDirty(false);
            return true;
        } catch (e) {
            return false;
        }
    };

    const updateFieldValuesByStaffType = (
        selectedStaffType: WorkloadStaffType,
        categoryName: string | null,
        setFieldValue: (
            field: string,
            value:
                | FormikValues['teaching']
                | FormikValues['service']
                | FormikValues['research']
                | FormikValues['senior']
                | FormikValues['professional']
                | FormikValues['staffType'],
        ) => void,
    ) => {
        if (!allocationsDirty) {
            const fieldValues =
                selectedStaffType === WorkloadStaffType.ACADEMIC
                    ? defaultWorkloadAllocationValues[selectedStaffType](categoryName || 'Teaching and Research')
                    : defaultWorkloadAllocationValues[selectedStaffType];

            Object.entries(fieldValues).forEach(([field, value]) => {
                setFieldValue(field, value);
            });
        }
        setFieldValue('staffType', selectedStaffType);
    };

    const updateStaffType = (values: UpdateWorkloadFormData) => {
        const academic = parseFloat(values.teaching) + parseFloat(values.research) + parseFloat(values.service);
        const professional = parseFloat(values.professional);
        const senior = parseFloat(values.senior);

        if (academic === 100) {
            return WorkloadStaffType.ACADEMIC;
        } else if (professional === 100) {
            return WorkloadStaffType.PROFESSIONAL;
        } else if (senior === 100) {
            return WorkloadStaffType.SENIOR;
        } else {
            return values.staffType;
        }
    };

    const handleUpdatedStaffType = (
        values: UpdateWorkloadFormData,
        setFieldValue: (field: string, value: FormikValues['staffType'], shouldValidate?: boolean) => void,
    ) => {
        if (!allocationsDirty) {
            const newStaffType = updateStaffType(values);
            setFieldValue('staffType', newStaffType);
        }
    };

    const formik = useRef<FormikProps<UpdateWorkloadFormData>>(null);
    useImperativeHandle(ref, () => ({
        getValues: () => (formik.current ? getDto(formik.current.values).workload : undefined),
        submit: async () => (formik.current ? await handleSubmit(formik.current.values, formik.current) : false),
    }));

    return (
        <Formik innerRef={formik} initialValues={getFormData(workload)} onSubmit={handleSubmit} validate={validate}>
            {({ dirty, errors, values, setFieldValue }) => {
                return (
                    <Form>
                        <VStack alignItems={'flex-start'} gap={5}>
                            <Flex>
                                <LabeledSelect
                                    label={'Staff Type'}
                                    labelProps={{ fontWeight: '500' }}
                                    value={values.staffType}
                                    onChange={(e) => {
                                        const selectedStaffType = e.target.value as WorkloadStaffType;
                                        const categoryName =
                                            selectedStaffType === WorkloadStaffType.ACADEMIC
                                                ? workload.workloadCategory
                                                : null;
                                        updateFieldValuesByStaffType(
                                            selectedStaffType,
                                            categoryName ?? null,
                                            setFieldValue,
                                        );
                                    }}
                                    isDisabled={loading}
                                >
                                    <option key={WorkloadStaffType.ACADEMIC} value={WorkloadStaffType.ACADEMIC}>
                                        {mapWorkloadStaffTypeToString[WorkloadStaffType.ACADEMIC]}
                                    </option>
                                    <option key={WorkloadStaffType.PROFESSIONAL} value={WorkloadStaffType.PROFESSIONAL}>
                                        {mapWorkloadStaffTypeToString[WorkloadStaffType.PROFESSIONAL]}
                                    </option>
                                    <option key={WorkloadStaffType.SENIOR} value={WorkloadStaffType.SENIOR}>
                                        {mapWorkloadStaffTypeToString[WorkloadStaffType.SENIOR]}
                                    </option>
                                </LabeledSelect>
                            </Flex>
                            <VStack alignItems={'flex-start'} gap={2}>
                                <Heading as="h3" size={'md'}>
                                    Academic allocation:
                                </Heading>
                                <SimpleGrid alignItems="center" rowGap={2} columnGap={2} gridTemplateColumns="auto 1fr">
                                    <WorkloadNumberField
                                        name="teaching"
                                        label="Teaching"
                                        prefix="%"
                                        onChange={(valueAsString) => {
                                            setFieldValue('teaching', valueAsString);
                                            handleUpdatedStaffType(
                                                { ...values, teaching: valueAsString },
                                                setFieldValue,
                                            );
                                            setAllocationsDirty(true);
                                        }}
                                    />
                                    <WorkloadNumberField
                                        name="research"
                                        label="Research"
                                        prefix="%"
                                        onChange={(valueAsString) => {
                                            setFieldValue('research', valueAsString);
                                            handleUpdatedStaffType(
                                                { ...values, research: valueAsString },
                                                setFieldValue,
                                            );
                                            setAllocationsDirty(true);
                                        }}
                                    />
                                    <WorkloadNumberField
                                        name="service"
                                        label="Service"
                                        prefix="%"
                                        onChange={(valueAsString) => {
                                            setFieldValue('service', valueAsString);
                                            handleUpdatedStaffType(
                                                { ...values, service: valueAsString },
                                                setFieldValue,
                                            );
                                            setAllocationsDirty(true);
                                        }}
                                    />
                                </SimpleGrid>
                            </VStack>
                            <VStack alignItems="flex-start" gap={2}>
                                <Heading as="h3" size={'md'}>
                                    Other allocation:
                                </Heading>
                                <SimpleGrid alignItems="center" rowGap={2} columnGap={2} gridTemplateColumns="auto 1fr">
                                    <WorkloadNumberField
                                        name="senior"
                                        label="Senior Staff"
                                        prefix="%"
                                        onChange={(valueAsString) => {
                                            setFieldValue('senior', valueAsString);
                                            handleUpdatedStaffType({ ...values, senior: valueAsString }, setFieldValue);
                                            setAllocationsDirty(true);
                                        }}
                                    />
                                    <WorkloadNumberField
                                        name="professional"
                                        label="Professional Staff"
                                        prefix="%"
                                        onChange={(valueAsString) => {
                                            setFieldValue('professional', valueAsString);
                                            handleUpdatedStaffType(
                                                { ...values, professional: valueAsString },
                                                setFieldValue,
                                            );
                                            setAllocationsDirty(true);
                                        }}
                                    />
                                </SimpleGrid>
                            </VStack>
                            {!!putError && (
                                <SimpleAlert variant="subtle">
                                    Could not update Workload.
                                    <br />
                                    Please try again later.
                                </SimpleAlert>
                            )}
                            <HStack justifyContent="flex-start" w="full">
                                {saveButton && (
                                    <Button
                                        colorScheme="blue"
                                        isLoading={putLoading}
                                        isDisabled={!dirty || (errors && !!Object.keys(errors).length)}
                                        type="submit"
                                        m={0}
                                    >
                                        Save
                                    </Button>
                                )}
                                {responseData && !dirty && <Text>Saved</Text>}
                                {errors &&
                                    (
                                        errors as {
                                            total: string;
                                        }
                                    ).total && (
                                        <SimpleAlert variant="subtle">
                                            {
                                                (
                                                    errors as {
                                                        total: string;
                                                    }
                                                ).total
                                            }
                                        </SimpleAlert>
                                    )}
                                {errors &&
                                    (
                                        errors as {
                                            academic: string;
                                        }
                                    ).academic && (
                                        <SimpleAlert variant="subtle">
                                            {
                                                (
                                                    errors as {
                                                        academic: string;
                                                    }
                                                ).academic
                                            }
                                        </SimpleAlert>
                                    )}
                            </HStack>
                        </VStack>
                    </Form>
                );
            }}
        </Formik>
    );
});
WorkloadEditorForm.displayName = 'WorkloadEditorForm';

export default WorkloadEditorForm;
